import { MailOutlined, BellOutlined } from "@ant-design/icons";
import { formatDateTimeAndTimezone } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { TimezoneKey } from "@impero-com/common/utils/timezone";
import { FormLabel, IconTimeline, TextOverrideSkeleton } from "@impero-com/ui";
import { Typography } from "antd";
import moment from "moment-timezone";
import { Control, useWatch } from "react-hook-form";

import { StepAllocatedTime } from "api/generated/model";
import { controlAssignmentDataToControlAssignmentGroups } from "utils/controls/assignmentUtils";

import { SimpleAllocatedTime } from "../formData";
import { ControlViewFormSchema } from "../formSchema";
import {
    ParsedControlSchedulingAndSteps,
    useNextOccurrenceQuery,
} from "../queries/useNextOccurrenceQuery";
import { formatPeriodStrings } from "../utils/formatPeriodStrings";
import { hoursToMicros } from "../utils/microsecondsToTime";

import * as theme from "./ControlTimeline.theme.css";

const { Text } = Typography;

const toStepAllocatedTime = (
    simpleAllocatedTime: SimpleAllocatedTime
): StepAllocatedTime => {
    switch (simpleAllocatedTime.unit) {
        case "days": {
            return {
                days: simpleAllocatedTime.value,
                microseconds: 0,
            };
        }
        case "hours": {
            return {
                days: 0,
                microseconds: hoursToMicros(simpleAllocatedTime.value),
            };
        }
        default: {
            return assertNever(simpleAllocatedTime.unit);
        }
    }
};

export function ControlFormTimeline({
    control,
}: {
    control: Control<ControlViewFormSchema>;
}) {
    const { t } = useTranslation();
    const [
        schedulingFields,
        allocatedTimes,
        remindersBefore,
        remindersAfter,
        assignmentsVal,
        sendImmediately,
    ] = useWatch({
        control,
        name: [
            "scheduling",
            "allocatedTimes",
            "remindersBefore",
            "remindersAfter",
            "controlAssignment",
            "sendImmediately",
        ],
    });

    const scheduling = {
        ...schedulingFields,
        timezoneName: schedulingFields.timezoneName as TimezoneKey,
        dueTime: moment(schedulingFields.dueTime),
        startDate: moment(schedulingFields.startDate),
        sendTime: moment(allocatedTimes.sendTime),
        remindersBefore,
        remindersAfter,
    };

    const parsedControlSchedulingAndSteps: ParsedControlSchedulingAndSteps = {
        scheduling,
        stepAllocatedTimes: [
            toStepAllocatedTime({
                value: allocatedTimes.responsibleDays,
                unit: "days",
            }),
            toStepAllocatedTime(allocatedTimes.firstReviewer),
            toStepAllocatedTime(allocatedTimes.secondReviewer),
        ],
    };

    const { data, status } = useNextOccurrenceQuery(
        parsedControlSchedulingAndSteps
    );

    if (status === "error") {
        return <Text>{t("Control_SchedulePreview_Error")}</Text>;
    }

    const isPending = status === "pending";
    const isNotApplicable =
        status === "success" && data.type === "NOT_APPLICABLE";

    const assignmentOccurrences = data?.nextOccurrence?.assignmentOccurrences;
    const getItemDate = (occurrence: string | undefined, now: boolean) => {
        if (now) {
            return t("Control_SchedulePreview_Now");
        }
        return occurrence
            ? formatDateTimeAndTimezone(occurrence, {
                  name: scheduling.timezoneName,
              })
            : undefined;
    };

    const withoutReviewOccurrences = assignmentOccurrences?.one;
    const withoutReviewItems = [
        {
            date: getItemDate(withoutReviewOccurrences?.[0], sendImmediately),
            label: t("Control_SchedulePreview_SendToResponsible"),
            icon: <MailOutlined />,
        },
        {
            date: getItemDate(withoutReviewOccurrences?.[1], false),
            label: t("Control_SchedulePreview_DueDate"),
            icon: <BellOutlined />,
        },
    ];

    const hasReviewOccurrences = assignmentOccurrences?.two;
    const hasReviewItems = [
        {
            date: getItemDate(hasReviewOccurrences?.[0], sendImmediately),
            label: t("Control_SchedulePreview_SendToResponsible"),
            icon: <MailOutlined />,
        },
        {
            date: getItemDate(hasReviewOccurrences?.[1], false),
            label: t("Control_SchedulePreview_ResponsibleDueDate"),
            icon: <BellOutlined />,
        },
        {
            date: getItemDate(hasReviewOccurrences?.[2], false),
            label: t("Control_SchedulePreview_ReviewDueDate"),
            icon: <BellOutlined />,
        },
    ];

    const hasSecondsReviewOccurrences = assignmentOccurrences?.three;
    const hasSecondReviewItems = [
        {
            date: getItemDate(
                hasSecondsReviewOccurrences?.[0],
                sendImmediately
            ),
            label: t("Control_SchedulePreview_SendToResponsible"),
            icon: <MailOutlined />,
        },
        {
            date: getItemDate(hasSecondsReviewOccurrences?.[1], false),
            label: t("Control_SchedulePreview_ResponsibleDueDate"),
            icon: <BellOutlined />,
        },
        {
            date: getItemDate(hasSecondsReviewOccurrences?.[2], false),
            label: t("Control_SchedulePreview_ReviewDueDate"),
            icon: <BellOutlined />,
        },
        {
            date: getItemDate(hasSecondsReviewOccurrences?.[3], false),
            label: t("Control_SchedulePreview_SecondReviewDueDate"),
            icon: <BellOutlined />,
        },
    ];

    const assignmentGroups =
        controlAssignmentDataToControlAssignmentGroups(assignmentsVal);
    const hasNoAssignments = assignmentGroups.length === 0;
    if (hasNoAssignments) {
        return (
            <div className={theme.controlTimelineNoAssignments}>
                <Text strong type="secondary">
                    {t("Control_SchedulePreview_NoAssignmentsTitle")}
                </Text>
                <Text type="secondary">
                    {t("Control_SchedulePreview_NoAssignmentsText")}
                </Text>
            </div>
        );
    }

    const hasNoReviewerAssignments = assignmentGroups.some(
        (group) => !group.firstReviewer
    );
    const hasFirstReviewerAssignments = assignmentGroups.some(
        (group) => group.firstReviewer && !group.secondReviewer
    );
    const hasSecondReviewerAssignments = assignmentGroups.some(
        (group) => group.secondReviewer
    );

    const hasCoveredPeriod = Boolean(scheduling.coveredPeriod);

    return (
        <div>
            {hasCoveredPeriod && (
                <div>
                    <FormLabel disabled>
                        {t("Control_SchedulePreview_CoveredPeriod")}
                    </FormLabel>
                    {data?.nextOccurrence?.coveredPeriod ? (
                        <Text strong>
                            {
                                formatPeriodStrings(
                                    t,
                                    data?.nextOccurrence?.coveredPeriod
                                ).label
                            }
                        </Text>
                    ) : (
                        <TextOverrideSkeleton
                            enableAnimation={isPending}
                            width={120}
                            overrideText={
                                isNotApplicable
                                    ? t("Control_SchedulePreview_NotApplicable")
                                    : undefined
                            }
                        />
                    )}
                </div>
            )}

            {hasNoReviewerAssignments && (
                <IconTimeline
                    label={t("Control_SchedulePreview_WithoutReview")}
                    items={withoutReviewItems}
                />
            )}
            {hasFirstReviewerAssignments && (
                <IconTimeline
                    label={t("Control_SchedulePreview_HasReview")}
                    items={hasReviewItems}
                />
            )}
            {hasSecondReviewerAssignments && (
                <IconTimeline
                    label={t("Control_SchedulePreview_HasSecondReview")}
                    items={hasSecondReviewItems}
                />
            )}
        </div>
    );
}
